﻿import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IFileIdFilter } from "@app/filters/file-id.filter";
import { Dirs } from "@app/helpers/dirs";
import { DialogService } from "@app/services/dialog.service";
import { DragAndDropService } from "@app/services/drag-and-drop.service";
import { DrawerService } from "@app/services/drawer.service";
import { KbService } from "@app/services/kb.service";
import { UploadService } from "@app/services/upload.service";
import { ApiService } from "@app/services/api.service";
import { DialogButton, eAlertType, IDialog, IMediaObject, InputDialogButton, IRootScope } from "@models";
import { icons } from "@tools";
import * as angular from "angular";
import ng from "angular";

export interface IMediaManagerScope extends ng.IScope {
    isDragging: boolean;
    isDialog: boolean;
    apiPath: string;
    rootDir: string;
    startPath: string;

    isReadOnly: boolean;

    addFolder: () => void;
    fileDoubleClicked: (file: IMediaObject) => void;
    open: (args: any) => void;
    upload: () => void;
    deleteItems: () => void;
    refresh: () => void;
    files: IMediaObject[];
    // uploadOptions: kendo.ui.UploadOptions;
    // listSource: kendo.data.DataSource;
    crumbs: ICrumb[];
    crumbClick: (crumb: ICrumb) => void;
    selectedFiles: IMediaObject[];
    selectedFile: IMediaObject;
    multiple: boolean;

    uploadingFiles: any[];
    uploading: boolean;
    addBlank: (ext: string) => void;
    download: (file: IMediaObject) => void;

    tracker: ng.IPromiseTracker; // tracker is passed in from the outside.  It should be used when making http calls
}

export interface ICrumb {
    url: string;
    name: string;
    icon: string;
}
    
@NgComponent({
    token: Token.KbMediaManager,
    dependencies: [
        Token.$Window,
        Token.$Http,
        Token.KbService,
        Token.DialogService,
        Token.$RootScope,
        Token.DrawerService,
        Token.DragAndDropService,
        Token.UploadService,
        Token.$Q,
        Token.$Filter,
        Token.$Timeout,
        Token.ApiService,
    ]
})
export class KbMediaManager extends Directive {

    constructor(
        private $window: ng.IWindowService,
        private $http: ng.IHttpService,
        private kbService: KbService,
        private dialogService: DialogService,
        private $rootScope: IRootScope,
        private drawerService: DrawerService,
        private dragAndDropService: DragAndDropService,
        private uploadService: UploadService,
        private $q: ng.IQService,
        private $filter: ng.IFilterService,
        private $timeout: ng.ITimeoutService,
        private api: ApiService) {

        super();

        this.restrict = "E";
        this.template = Dirs.component("kb-media-manager");
        this.scope = {
            dialog: "@",
            multiple: "=?",
            selectedFile: "=?",
            selectedFiles: "=?",
            filter: "@",
            apiPath: "@",
            rootDir: "@",
            startPath: "@",
            open: "&",
            tracker: "=?",
            isDragging: "=?"
        };
        this.replace = true;
            
        this.link = (scope: IMediaManagerScope, element, atts: any) => {

            this.atts = atts;
            this.mscope = scope;
            if (!this.mscope.apiPath) this.mscope.apiPath = "api/media";
            if (!this.mscope.rootDir) this.mscope.rootDir = "media";
            if (!this.mscope.tracker) this.mscope.tracker = this.$rootScope.contentTracker;

            if (typeof atts.multiple == "undefined") {
                scope.multiple = true;
            }

            scope.isDialog = (atts.dialog == "true");

            scope.isReadOnly = !this.$rootScope.user.canModifyMedia || this.$rootScope.environment != "dev";

            scope.deleteItems = () => {
                this.deleteSelectedListItems();
            };

            scope.refresh = () => {
                this.refreshList();
            };

            scope.addFolder = () => {
                this.addFolder();
            };

            scope.fileDoubleClicked = (file: IMediaObject) => {
                // if this is a folder, then navigate to the folder.  If a file, then call the handler of the consumer
                if (!file.isFile) {
                    this.navigate(file.relativePath);
                } else {
                    if (scope.open) {
                        scope.open({ file }); // call the handler of the consumer
                    }
                }
            };

            scope.upload = (accept?: string) => {
                this.uploadService.promptUserToChooseFiles().then(files => {
                    this.uploadFiles(files);
                });
            };

            scope.crumbClick = crumb => {
                this.navigate(crumb.url);
            };

            scope.addBlank = ext => {
                this.dialogService.input({
                    msg: "Select a name for the new file",
                    inputType: "text",
                    value: "New File",
                    buttons: [
                        new InputDialogButton(loc.ok, icons.success, args => {
                            if (args.value) {
                                $http.post("/api/templates/blank", {
                                    relativePath: this.currentDir + args.value + "." + ext
                                }, { tracker: scope.tracker }).then(response => {
                                    this.refreshList();
                                });
                            }
                        }),
                        new InputDialogButton(loc.cancel, icons.cancel, args => {

                        })
                    ]
                });
            };

            scope.download = (file: IMediaObject) => {
                if (file) {
                    if (file.isFile) {
                        scope.fileDoubleClicked(file);
                    } else {
                        let params = $.param({ relativePath: file.relativePath });
                        let url = this.mscope.apiPath + "/sizeInfo?" + params;
                        this.$http.get<any>(url, { tracker: this.mscope.tracker }).then(r => {
                            var warnMsg = null;

                            if (r.data.size > 500000000) {
                                warnMsg = loc.msg_warndownloadfoldersize.format("500 MB");
                            } else if (r.data.fileCount > 500) {
                                warnMsg = loc.msg_warndownloadfolderfilecount.format("500");
                            }

                            if (warnMsg) {
                                this.dialogService.dialog({
                                    content: warnMsg,
                                    buttons: [
                                        new DialogButton(loc.ok, icons.success, () => {
                                            this.downloadFolder(file.relativePath);
                                        }),
                                        new DialogButton(loc.cancel, icons.cancel, () => {
                                        })
                                    ]
                                });
                            } else {
                                this.downloadFolder(file.relativePath);
                            }
                        });
                    }
                }
            };

            scope.files = [];

            if (!scope.isDialog) {
                if ($rootScope.user.canModifyMedia && this.$rootScope.environment == "dev") {
                    let drAddFolder = drawerService.addDrawer(icons.addFolder, loc.addfolder, scope.addFolder);
                }

                let drOpen = drawerService.addDrawer(icons.open, loc.open, () => scope.fileDoubleClicked(this.selectedItem()));
                let drDownload = drawerService.addDrawer(icons.download, loc.download, () => scope.download(this.selectedItem()));

                if ($rootScope.user.canModifyMedia && this.$rootScope.environment == "dev") {
                    let drUpload = drawerService.addDrawer(icons.upload, loc.upload, scope.upload);

                    let drDelete = drawerService.addDrawer(icons.deleter, loc.deleter, scope.deleteItems);
                }
                let drRefresh = drawerService.addDrawer(icons.refresh, loc.refresh, scope.refresh);
            }

            // setup root crumb
            let startDir = scope.startPath ? this.getDirectory(scope.startPath) : "";
            this.navigate(startDir);


            const dragAndDrop = this.dragAndDropService.subscribe({
                onDropFiles: (files: File[]) => {
                    this.onDropFiles(files);
                    scope.isDragging = false;
                    scope.$applyAsync();
                }
            });

            scope.$on("globalDragStateChanged", (event, isDragging) => {
                scope.isDragging = isDragging;
                scope.$applyAsync();
            });

            scope.$on("$destroy", dragAndDrop);




                
        };
    }

    private currentDir: string;
    private mscope: IMediaManagerScope;
    private uploadDialog: IDialog;
    private atts;
    
    private getChildren = (dir: string) => {
        let params = $.param($.extend({dir}, { filter: this.atts.filter }));
        let url = this.mscope.apiPath + "/children?" + params;
        this.$http.get<IMediaObject[]>(url, { tracker: this.mscope.tracker }).then(r => {
            this.mscope.files = r.data;
            this.$timeout(() => {
                if (this.mscope.startPath) {
                    let startFile = this.mscope.files.find(f => f.relativePath == this.mscope.startPath);
                    if (startFile) this.mscope.selectedFile = startFile;
                    this.mscope.startPath = null;
                }
            }, 0);
        });
    }

    private getDirectory = (path: string) => {
        let index = path.indexOf("/");
        if (index > -1) {
            return path.substr(0, index);
        }
        return "";
    }
       
    private navigate(dir: string) {
        this.currentDir = dir;
        this.refreshCrumbs(dir );
        this.getChildren(dir);
        // this.mscope.listSource.read({ id: dir });            
    }

    private refreshCrumbs(dir: string) {
        this.mscope.crumbs = [];

        this.mscope.crumbs.push({ url: "", name: this.mscope.rootDir, icon: icons.image } as ICrumb);

        let segments: string[] = dir.split("/");

        for (let index = 0; index < segments.length; index++) {
            let segment = segments[index];
            if (segment) {
                // calculate the absolute url of the segment
                let url: string = "";
                for (let i = 0; i <= index; i++) {
                    url += segments[i] + "/";
                }
                this.mscope.crumbs.push({ url, name: segment, icon: icons.crumb } as ICrumb);
            }
        }
    }

    private deleteSelectedListItems() {
        let isTemplate = this.mscope.apiPath.toLowerCase().includes("template")

        let selected = this.getListViewSelectedItems();
        let ids = selected.map(m => (isTemplate ? "template/" : "media/") + m.relativePath);

        this.api.dependencies.isRequiredByDependency(isTemplate ? "Template" : "Media", ids, this.mscope.tracker).then(required => {
            this.dialogService.dialog({
                content: required ? loc.msg_deleterequiredbydependency : loc.msg_deletemedia,
                buttons: [
                    new DialogButton(loc.ok, icons.success, () => {
                        selected.forEach(mobject => {
                            this.$http.delete(this.mscope.apiPath,
                                {
                                    params: {
                                        relativePath: mobject.relativePath
                                    },
                                    headers:
                                    {
                                        "Content-Type": "application/json"
                                    }
                                });
                        });

                        // delete the stuff directly in the source so the user doesn't have to wait
                        $.each(selected, (index: number, mobject: IMediaObject) => {
                            // now remove the item from the listview
                            this.mscope.files.remove(mobject);
                            // (<any>this.mscope.listSource).remove(mobject);
                        });
                    }),
                    new DialogButton(loc.cancel, icons.cancel, args => {
                    })
                ]

            });
        });
    }
                
    private addFolder() {
        this.dialogService.input({
            msg: loc.msg_renamefolder,
            value: loc.newfolder,
            buttons: [
                new InputDialogButton(loc.ok, icons.success, args => {
                    this.$http.post(this.mscope.apiPath + "/addfolder", {
                        dir: this.currentDir, folderName: args.value
                    }, { tracker: this.mscope.tracker })
                        .then(() => {
                            this.refreshList();
                        });
                }),
                new InputDialogButton(loc.cancel, icons.cancel, args => {
                })

            ]

        });
    }

    private getListViewDataItem($domItem: JQuery): IMediaObject {
        let index: number = $domItem.index();
        return null;
        // return this.mscope.listSource.view()[index];
    }

    private getListViewSelectedItems(): IMediaObject[] {
        if (this.mscope.multiple) {
            return this.mscope.selectedFiles;
        }
        return [this.mscope.selectedFile];
    }

    public selectedItem(): IMediaObject {
        let selected = this.getListViewSelectedItems();
        if (selected) {
            return selected[0];
        }
        return null;
    }

    private refreshList() {
        this.getChildren(this.currentDir);
    }

    private onUpload(e) {
        if (!this.mscope.uploadingFiles) this.mscope.uploadingFiles = [];

        angular.forEach(e.files, (item, index) => {
            item.percentComplete = 0;
            this.mscope.uploadingFiles.push(item);
        });

        if (!this.mscope.uploading) {
                
            this.uploadDialog = this.dialogService.dialog({
                template: Dirs.view("dialog-upload-files"),
                scope: this.mscope.$new(),
                buttons: [
                    new InputDialogButton(loc.ok, icons.success, args => {
                        this.mscope.uploadingFiles = null;
                        this.refreshList();
                    }, false) // hide the ok button by passing in visible = false
                ]
            });
                
        }

        this.mscope.uploading = true;
    }

    private uploadProgress(e) {
        $.each(e.files, (index, file) => {
            let $li = $("#uploading-list #" + ((this.$filter as any)("fileId") as IFileIdFilter)(file.name));
            file.percentComplete = e.percentComplete;
        });
    }

    private uploadError(e) {
        $.each(e.files, (index, file: File) => {
            let $li = $("#uploading-list #" + ((this.$filter as any)("fileId") as IFileIdFilter)(file.name));
            $li.prepend('<div class="kb-icon error"><div class="kb-icon__glyph">' + icons.error + "</div></div>");
        });
    }

    private uploadComplete(e?) {
        this.mscope.uploading = false;
        // show the buttons in the dialog now\
        this.uploadDialog.buttons[0].visible = true;
    }

    private downloadFolder(relativePath: string) {
        let relPath = encodeURIComponent(relativePath);
        window.open(this.mscope.apiPath + "/downloadFolder?relativePath=" + relPath, "_blank");
    }

    private uploadFiles(files: File[]) {
        this.onUpload({ files });

        let promises = files.map(file => {
            let p = this.uploadService.uploadFileToMedia(file, this.currentDir, this.mscope.apiPath);

            return p.then(() => {
                this.uploadProgress({ files: [file], percentComplete: 100 });
            }, () => {
                this.uploadError({ files: [file] });
            }, (e: ProgressEvent) => {
                this.uploadProgress({
                    files: [file],
                    percentComplete: ((e.loaded / e.total) * 100)
                });
            });
        });

        return this.$q.all(promises).finally(() => this.uploadComplete());
    }

    private onDropFiles(files: File[]) {
        if (this.$rootScope.environment === "dev") {
            this.uploadFiles(files);
        }
    }
}
